import { observer } from "mobx-react-lite";
import { Button, cn, PopoverTrigger } from "ui";

import { ExerciseWorkoutItemActionsProvider } from "src/components/ExerciseWorkoutItemActionsProvider";
import { WorkoutItemActions } from "src/components/WorkoutItemActionsMenu";
import { WorkoutItemAlternates } from "src/components/WorkoutItemAlternates";
import { ExerciseCombobox } from "src/workouts/ExerciseCombobox";
import { useExerciseAlternateActions } from "src/workouts/useExerciseAlternateActions";
import { useReplaceExercise } from "src/workouts/useReplaceExercise";
import { useWorkoutEditStore } from "src/workouts/WorkoutEditContext";
import { type WorkoutItemLayout } from "src/workouts/WorkoutEditStore";
import { useParentSectionGroupType } from "src/workouts/WorkoutItemGroupContext";
import { getWorkoutItemElementId } from "src/workouts/workoutItemScroll";
import { WorkoutItemSets } from "src/workouts/WorkoutItemSets";
import { useWorkoutPermissions } from "src/workouts/WorkoutItemsPermission";
import { WorkoutItemWrapper } from "src/workouts/WorkoutItemWrapper";
import { useWorkoutPlugins } from "src/workouts/WorkoutPlugins";

export interface NormalWorkoutItemProps {
  id: string;
  index: number;
  item: WorkoutItemLayout;
  level: number;
  isLast: boolean;
}

export const NormalWorkoutItem = observer(function NormalWorkoutItem(
  props: NormalWorkoutItemProps,
) {
  const workoutItemsPermission = useWorkoutPermissions();

  const workoutEditStore = useWorkoutEditStore();
  const workoutItem = workoutEditStore.getWorkoutItemById(
    props.item.workoutItemId,
  );
  const exercise = workoutItem.exercise!;
  const activeWorkoutItemId = workoutEditStore.getActiveWorkoutItemId(
    workoutItem.id,
  );
  const activeExercise =
    workoutEditStore.getWorkoutItemExercise(activeWorkoutItemId) ?? exercise;

  const alternateActions = useExerciseAlternateActions({
    workoutId: workoutEditStore.workout.id,
    workoutItemId: workoutItem.id,
  });
  const replaceExercise = useReplaceExercise(workoutItem.id);

  const workoutPlugins = useWorkoutPlugins();
  const parentGroupType = useParentSectionGroupType();

  const parentWorkoutItem = workoutItem.parentId
    ? workoutEditStore.getWorkoutItemById(workoutItem.parentId)
    : null;

  const isParentGroupFreestyle =
    parentWorkoutItem?.type === "GROUP" &&
    parentWorkoutItem.format === "FREESTYLE";

  const shouldShowSets =
    workoutItemsPermission.workoutItemSetShown && !isParentGroupFreestyle;

  return (
    <WorkoutItemWrapper
      className={cn("flex flex-col rounded-md group bg-white", {
        "rounded-none first:rounded-t-md": parentGroupType === "SUPER_SET",
        "rounded-b-md": parentGroupType === "SUPER_SET" && props.isLast,
      })}
      id={getWorkoutItemElementId(workoutItem.id)}
    >
      <WorkoutItemAlternates
        workoutItemId={workoutItem.id}
        activeWorkoutItemId={activeWorkoutItemId}
        mainExercise={exercise}
        alternates={workoutEditStore.getAlternatesForWorkoutItem(
          workoutItem.id,
        )}
        canEdit={workoutItemsPermission.canEditItems}
        actions={alternateActions}
      />
      {/* Edits made while a swap is pending would land on the swapped
          exercise. */}
      <fieldset disabled={alternateActions.isMakingMain} className="contents">
        <div
          key={activeWorkoutItemId}
          className={"relative flex gap-2 p-2 items-center justify-between"}
        >
          <div className="flex w-full">
            {workoutItemsPermission.canEditItems ? (
              <ExerciseCombobox
                onChange={(value) => {
                  return replaceExercise({
                    workoutItemId: activeWorkoutItemId,
                    exerciseId: value.id,
                  });
                }}
                value={activeExercise}
                trigger={
                  <PopoverTrigger asChild>
                    <Button
                      disabled={!workoutItemsPermission.canEditItems}
                      role="combobox"
                      className="truncate min-w-0 justify-between border-none font-medium shadow-none rounded-md w-full"
                      variant="outline"
                    >
                      <span>{activeExercise.name}</span>
                    </Button>
                  </PopoverTrigger>
                }
              />
            ) : (
              <div className={"items-center flex gap-4"}>
                <div>{activeExercise.name}</div>
              </div>
            )}
          </div>
          {workoutPlugins.comments && workoutItemsPermission.canAddComments ? (
            <workoutPlugins.comments workoutItemId={workoutItem.id} />
          ) : null}
          <ExerciseWorkoutItemActionsProvider workoutItemId={workoutItem.id}>
            <WorkoutItemActions />
          </ExerciseWorkoutItemActionsProvider>
        </div>
        {shouldShowSets ? (
          <WorkoutItemSets workoutItemId={activeWorkoutItemId} />
        ) : null}
        {workoutPlugins.history ? (
          <workoutPlugins.history
            workoutItemId={activeWorkoutItemId}
            exerciseId={activeExercise.id}
          />
        ) : null}
      </fieldset>
    </WorkoutItemWrapper>
  );
});
